import { CSSProperties, useEffect, useState } from "react";
import { api } from "../api.js";
import { useIsModern } from "../theme.js";
import { MastersTabModern } from "./Masters.modern.js";

interface MasterRow {
  id: string;
  name: string;
  telegramId: string | null;
  telegramUsername: string | null;
  active: boolean;
  starred: boolean;
  lastVisitAt: string | null;
}

import {
  PeriodPicker,
  periodLabel,
  periodToIsoRange,
  type PeriodValue,
} from "../components/PeriodPicker.js";

type Range = PeriodValue;
type Source = "cash" | "bumpix";
const RANGE_LABEL = (r: Range) => periodLabel(r);

const SERVICE_LABEL: Record<string, string> = {
  manicure: "Манікюр",
  pedicure: "Педикюр",
  manicure_pedicure: "Манік + педик",
  other: "Інше",
};

const STATUS_LABEL: Record<string, string> = {
  completed: "✅",
  scheduled: "🕒",
  cancelled: "❌",
  no_show: "🚫",
};

function rangeToDates(r: Range): { from: string; to: string } {
  return periodToIsoRange(r);
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${String(d.getFullYear()).slice(2)}`;
}

// Точна сума до 1 грн з розділювачем тисяч ("16 057", не "16k") — люди
// плутались у заокруглених «k». Групування пробілами, знак зберігається.
function uahFmt(n: number) {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Full amount with space-grouped thousands ("10 200") — locale-free for iOS. */
function uahFull(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

type MastersView = "list" | "reports";

export function MastersTab() {
  const modern = useIsModern();
  if (modern) return <MastersTabModern />;
  return <LegacyMastersTab />;
}

function LegacyMastersTab() {
  const [view, setView] = useState<MastersView>("list");
  const [items, setItems] = useState<MasterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [tgId, setTgId] = useState("");
  const [tgUser, setTgUser] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [canSeeReports, setCanSeeReports] = useState(false);
  const [reportsMaster, setReportsMaster] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const load = () =>
    api
      .masters()
      .then((data) => setItems(data as MasterRow[]))
      .catch((e) => setError(String(e)));

  useEffect(() => {
    void load();
    // Cash reports видно і адмінці, і власниці/dev — з однаковими правами
    // на редагування (v0.28.0). Owner-only лишились тільки виплати й аванси.
    api
      .me()
      .then((m) => setCanSeeReports(m.role === "admin"))
      .catch(() => {});
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.upsertMaster({
        name: name.trim(),
        telegramId: tgId.trim() || undefined,
        telegramUsername: tgUser.trim() || undefined,
      });
      setName("");
      setTgId("");
      setTgUser("");
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  }

  // Detail/Report overlays sit on top; list+ tabs hide via display:none
  // (NOT unmount) so scroll + state survive when user comes back.
  const overlayOpen = !!reportsMaster || !!selectedId;
  return (
    <>
      {reportsMaster && (
        <MasterCashReports
          masterId={reportsMaster.id}
          masterName={reportsMaster.name}
          onBack={() => setReportsMaster(null)}
        />
      )}
      {!reportsMaster && selectedId && (
        <MasterDetail id={selectedId} onBack={() => setSelectedId(null)} />
      )}
      <div style={{ display: overlayOpen ? "none" : "block" }}>
        {canSeeReports && (
          <div className="tabs" style={{ marginBottom: 8 }}>
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
            >
              👩‍🎨 Майстри
            </button>
            <button
              className={view === "reports" ? "active" : ""}
              onClick={() => setView("reports")}
            >
              📋 Звіти
            </button>
          </div>
        )}

        {view === "reports" && canSeeReports ? (
          <AllCashReportsView />
        ) : (
          <MastersList
            items={items}
            name={name}
            setName={setName}
            tgId={tgId}
            setTgId={setTgId}
            tgUser={tgUser}
            setTgUser={setTgUser}
            saving={saving}
            add={add}
            error={error}
            canSeeReports={canSeeReports}
            load={load}
            setSelectedId={setSelectedId}
            setReportsMaster={setReportsMaster}
          />
        )}
      </div>
    </>
  );
}

function MastersList({
  items,
  name,
  setName,
  tgId,
  setTgId,
  tgUser,
  setTgUser,
  saving,
  add,
  error,
  canSeeReports,
  load,
  setSelectedId,
  setReportsMaster,
}: {
  items: MasterRow[] | null;
  name: string;
  setName: (s: string) => void;
  tgId: string;
  setTgId: (s: string) => void;
  tgUser: string;
  setTgUser: (s: string) => void;
  saving: boolean;
  add: (e: React.FormEvent) => Promise<void>;
  error: string | null;
  canSeeReports: boolean;
  load: () => Promise<unknown>;
  setSelectedId: (id: string) => void;
  setReportsMaster: (m: { id: string; name: string } | null) => void;
}) {
  return (
    <div>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Додати майстра</h2>
        <form onSubmit={add}>
          <input
            type="text"
            placeholder="Імʼя (напр. Маша)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Telegram ID (число)"
            value={tgId}
            onChange={(e) => setTgId(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            style={inputStyle}
          />
          <input
            type="text"
            placeholder="Telegram username (без @)"
            value={tgUser}
            onChange={(e) => setTgUser(e.target.value.replace(/^@/, ""))}
            style={inputStyle}
          />
          <button type="submit" disabled={saving || !name.trim()} style={buttonStyle}>
            {saving ? "Зберігаю..." : "Додати"}
          </button>
        </form>
        <p className="hint" style={{ marginTop: 8 }}>
          ID можна дізнатись командою <b>/myid</b> у нашому боті.
        </p>
      </div>

      {error && (
        <div className="card">
          <p style={{ color: "tomato" }}>{error}</p>
        </div>
      )}

      <h2>Майстри</h2>
      {!items ? (
        <div className="hint">Завантаження...</div>
      ) : items.length === 0 ? (
        <div className="card">
          <p>Поки нікого нема.</p>
        </div>
      ) : (
        items.map((m) => (
          <div key={m.id} style={{ marginBottom: 8 }}>
            <div style={{ ...masterCardWrap, marginBottom: 0 }}>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await api.setMasterStar(m.id, !m.starred);
                    await load();
                  } catch (err) {
                    alert(String(err));
                  }
                }}
                style={starBtn}
                title={m.starred ? "Прибрати з пріоритетних" : "Зробити пріоритетним"}
              >
                {m.starred ? "⭐" : "☆"}
              </button>
              <button
                onClick={() => setSelectedId(m.id)}
                style={{ ...masterCardBtn, flex: 1, marginBottom: 0 }}
              >
                <div className="row">
                  <strong>{m.name}</strong>
                  <span className="spacer" />
                  <span className="hint">
                    {m.telegramUsername ? `@${m.telegramUsername}` : ""}
                  </span>
                </div>
                <div className="hint" style={{ marginTop: 4, fontSize: 12 }}>
                  {m.lastVisitAt
                    ? `Останній візит: ${formatShortDate(m.lastVisitAt)}`
                    : "Без візитів"}
                  {m.telegramId ? ` · TG ${m.telegramId}` : ""}
                  {!m.active ? " · неактивний" : ""}
                </div>
              </button>
            </div>
            {canSeeReports && (
              <button
                onClick={() => setReportsMaster({ id: m.id, name: m.name })}
                style={reportsBtn}
              >
                📋 Звіти каси (переглянути / редагувати)
              </button>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export function MasterDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [range, setRange] = useState<Range>({ preset: "1m" });
  const [source, setSource] = useState<Source>("cash");
  const [data, setData] = useState<any | null>(null);
  const [photos, setPhotos] = useState<any[] | null>(null);
  const [photoTotal, setPhotoTotal] = useState(0);
  const [photoPage, setPhotoPage] = useState(1);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [visitsShown, setVisitsShown] = useState(10);
  const [payoutsShown, setPayoutsShown] = useState(10);
  const [topClientsShown, setTopClientsShown] = useState(10);
  const [serviceMixShown, setServiceMixShown] = useState(10);
  const [error, setError] = useState<string | null>(null);

  // 12 per page — matches the 3-col grid cleanly (4 rows × 3 photos).
  const PHOTOS_PER_PAGE = 12;
  const PAGE_STEP = 10;

  function load() {
    const { from, to } = rangeToDates(range);
    api
      .masterStats(id, from, to)
      .then(setData)
      .catch((e) => setError(String(e)));
  }

  useEffect(() => {
    setData(null);
    setVisitsShown(10);
    setPayoutsShown(10);
    setTopClientsShown(10);
    setServiceMixShown(10);
    load();
  }, [id, range]);

  useEffect(() => {
    setPhotoPage(1);
  }, [id]);

  // Портфоліо — сторінками з сервера. Раніше бралось перших 100 фото і
  // різалось на клієнті, тому все старше сотні було недосяжне.
  useEffect(() => {
    let cancelled = false;
    setPhotoBusy(true);
    api
      .masterPhotos(id, PHOTOS_PER_PAGE, (photoPage - 1) * PHOTOS_PER_PAGE)
      .then((r) => {
        if (cancelled) return;
        setPhotos(r.items as any[]);
        setPhotoTotal(r.total);
      })
      .catch(() => {
        if (cancelled) return;
        setPhotos([]);
        setPhotoTotal(0);
      })
      .finally(() => {
        if (!cancelled) setPhotoBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, photoPage]);

  if (error) {
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="card">
          <p>Помилка: {error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="hint">Завантаження...</div>
      </div>
    );
  }

  const hasCashData = data.cashEntries > 0;
  const effectiveSource: Source = source === "cash" && !hasCashData ? "bumpix" : source;
  const revenue =
    effectiveSource === "cash" ? data.cashRevenue : data.bumpixRevenue;
  const avgCheck =
    effectiveSource === "cash" ? data.cashAvgCheck : data.bumpixAvgCheck;

  async function payOut() {
    if (data.pendingPayout <= 0) return;
    const prices = data.pendingFromPrices ?? 0;
    const tips = data.pendingTips ?? 0;
    const breakdown =
      prices > 0 && tips > 0
        ? `${uahFull(prices)} грн з послуг + ${uahFull(tips)} грн чайові`
        : tips > 0
          ? `${uahFull(tips)} грн чайові`
          : `${uahFull(prices)} грн`;
    const ok = window.confirm(
      `Виплатити ${breakdown} для ${data.master.name}?`,
    );
    if (!ok) return;
    try {
      const { from, to } = rangeToDates(range);
      await api.payoutMaster(id, from, to);
      load();
    } catch (e) {
      alert(String(e));
    }
  }

  return (
    <div>
      <BackBtn onBack={onBack} />

      <div className="card">
        <h1 style={{ margin: "0 0 4px", fontSize: 20 }}>
          {data.master.name}
          {!data.master.active && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 600,
                color: "#b91c1c",
                background: "#fee2e2",
                padding: "2px 8px",
                borderRadius: 6,
                verticalAlign: "middle",
              }}
            >
              🔒 НЕАКТИВНА
            </span>
          )}
        </h1>
        <div className="hint">
          {data.master.telegramUsername && <>@{data.master.telegramUsername} · </>}
          {data.master.telegramId && <>TG: {data.master.telegramId}</>}
        </div>

        <button
          onClick={async () => {
            const turnOff = data.master.active;
            const verb = turnOff ? "закрити доступ" : "відновити доступ";
            if (!confirm(`Точно ${verb} цій майстерці?`)) return;
            try {
              await api.setMasterActive(id, !turnOff);
              load();
            } catch (e) {
              alert(String(e));
            }
          }}
          className="btn-secondary"
          style={{
            marginTop: 10,
            width: "100%",
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 600,
            color: data.master.active ? "#b91c1c" : "#065f46",
            background: data.master.active ? "#fee2e2" : "#d1fae5",
            border: 0,
          }}
        >
          {data.master.active
            ? "🔒 Закрити доступ до бота"
            : "🔓 Відновити доступ до бота"}
        </button>

        <button
          onClick={async () => {
            const cur = data.master.telegramId ?? "";
            const input = window.prompt(
              "Telegram ID майстра (хай напише боту /myid і скине число).\n" +
                "Порожнє = відвʼязати:",
              cur,
            );
            if (input === null) return;
            try {
              await api.setMasterTelegram(id, { telegramId: input.trim() });
              load();
            } catch (e) {
              alert(String(e));
            }
          }}
          className="btn-secondary"
          style={{
            marginTop: 8,
            width: "100%",
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 600,
            color: "#7c3aed",
            background: "#f5f3ff",
            border: 0,
          }}
        >
          {data.master.telegramId
            ? "📱 Змінити Telegram ID"
            : "📱 Задати Telegram ID"}
        </button>
      </div>

      <PeriodPicker value={range} onChange={setRange} />

      <div className="tabs" style={{ marginBottom: 12 }}>
        <button
          className={source === "cash" ? "active" : ""}
          onClick={() => setSource("cash")}
        >
          💰 Telegram
        </button>
        <button
          className={source === "bumpix" ? "active" : ""}
          onClick={() => setSource("bumpix")}
        >
          📊 Bumpix
        </button>
      </div>

      {source === "cash" && !hasCashData && (
        <div className="card" style={{ background: "rgba(255,193,7,0.15)" }}>
          <p style={{ margin: 0 }}>
            Майстер ще не присилала касових звітів за цей період. Показую з
            Bumpix.
          </p>
        </div>
      )}

      <div style={gridStyle}>
        <StatBox label="Візитів" value={data.visits} hint={effectiveSource === "cash" ? `${data.cashEntries} записів каси` : "з Bumpix"} />
        <StatBox label="Клієнтів" value={data.uniqueClients} hint="унікальних" />
        <StatBox label="Нових" value={data.newClients} hint="вперше до неї" />
        <StatBox
          label="Виручка"
          value={uahFmt(revenue) + " грн"}
          hint={`ср. чек ${uahFmt(avgCheck)}`}
          color="#10b981"
        />
      </div>

      {data.retention && data.retention.newClients > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            📈 Retention · {RANGE_LABEL(range)}
          </h2>
          <div
            style={{
              fontSize: 38,
              fontWeight: 800,
              color:
                data.retention.rate >= 60
                  ? "#10b981"
                  : data.retention.rate >= 30
                    ? "#f59e0b"
                    : "#e74c3c",
              lineHeight: 1.1,
            }}
          >
            {data.retention.rate}%
          </div>
          <div className="hint" style={{ fontSize: 13, marginTop: 4 }}>
            <b>{data.retention.returned}</b> з <b>{data.retention.newClients}</b>{" "}
            новеньких клієнток повернулись до неї ще раз
          </div>
        </div>
      )}

      {effectiveSource === "cash" && (
        <>
          {/* Breakdown: master's 40% from services + tips + studio's 60%. */}
          <div style={breakdownGrid}>
            <StatBox
              label="Послуги (40%)"
              value={
                uahFmt(
                  data.masterServicesTakes ?? Math.round(data.cashRevenue * 0.4),
                ) + " грн"
              }
              hint="майстру з кас"
              color="#10b981"
            />
            <StatBox
              label="💸 Чайові"
              value={uahFmt(data.cashTips) + " грн"}
              hint="100% майстру"
              color="#10b981"
            />
            <StatBox
              label="Студії (60%)"
              value={uahFmt(data.studioTakes) + " грн"}
              hint="з послуг"
              color="#7c3aed"
            />
          </div>

          <PayoutPanel
            paidOut={data.paidOut}
            pendingFromPrices={data.pendingFromPrices ?? 0}
            pendingTips={data.pendingTips ?? 0}
            onPayOut={payOut}
          />
        </>
      )}

      {photoTotal > 0 && (() => {
        const totalPages = Math.max(1, Math.ceil(photoTotal / PHOTOS_PER_PAGE));
        const page = Math.min(photoPage, totalPages);
        return (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>📸 Портфоліо · {photoTotal}</h2>
            <div style={{ ...photoGrid, opacity: photoBusy ? 0.5 : 1 }}>
              {(photos ?? []).map((p) => (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener"
                  style={photoCell}
                >
                  <img
                    src={p.url}
                    alt={p.caption ?? ""}
                    loading="lazy"
                    style={photoImg}
                  />
                </a>
              ))}
            </div>
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 10,
                  gap: 8,
                }}
              >
                <button
                  className="btn-secondary"
                  onClick={() => setPhotoPage(page - 1)}
                  disabled={page <= 1 || photoBusy}
                  style={{ padding: "6px 14px" }}
                >
                  ← Назад
                </button>
                <span className="hint" style={{ fontSize: 13 }}>
                  стор. {page} з {totalPages}
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => setPhotoPage(page + 1)}
                  disabled={page >= totalPages || photoBusy}
                  style={{ padding: "6px 14px" }}
                >
                  Далі →
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {data.topClients.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            ⭐ Топ клієнти
            <span className="hint" style={{ fontWeight: 400, marginLeft: 6 }}>
              · {Math.min(topClientsShown, data.topClients.length)} з{" "}
              {data.topClients.length}
            </span>
          </h2>
          <p className="hint" style={{ marginBottom: 8 }}>
            За к-стю візитів до цієї майстерки (Bumpix)
          </p>
          {data.topClients.slice(0, topClientsShown).map((c: any) => (
            <div key={c.clientId} className="row" style={topRow}>
              <span>
                {c.firstName} {c.lastName ?? ""}
              </span>
              <span className="spacer" />
              <span className="hint" style={{ marginRight: 8 }}>{c.visits} віз.</span>
              <span style={{ fontWeight: 600 }}>{uahFmt(c.spent)} грн</span>
            </div>
          ))}
          {topClientsShown < data.topClients.length && (
            <button
              className="btn-secondary"
              onClick={() => setTopClientsShown(topClientsShown + PAGE_STEP)}
              style={loadMoreBtn}
            >
              ⬇ Завантажити ще (+
              {Math.min(PAGE_STEP, data.topClients.length - topClientsShown)})
            </button>
          )}
        </div>
      )}

      {data.serviceMix.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            💅 Послуги
            <span className="hint" style={{ fontWeight: 400, marginLeft: 6 }}>
              · {Math.min(serviceMixShown, data.serviceMix.length)} з{" "}
              {data.serviceMix.length}
            </span>
          </h2>
          {data.serviceMix.slice(0, serviceMixShown).map((s: any) => (
            <div key={s.service} className="row" style={topRow}>
              <span>{SERVICE_LABEL[s.service] ?? s.service}</span>
              <span className="spacer" />
              <span className="hint" style={{ marginRight: 8 }}>{s.visits} віз.</span>
              <span style={{ fontWeight: 600 }}>{uahFmt(s.revenue)} грн</span>
            </div>
          ))}
          {serviceMixShown < data.serviceMix.length && (
            <button
              className="btn-secondary"
              onClick={() => setServiceMixShown(serviceMixShown + PAGE_STEP)}
              style={loadMoreBtn}
            >
              ⬇ Завантажити ще (+
              {Math.min(PAGE_STEP, data.serviceMix.length - serviceMixShown)})
            </button>
          )}
        </div>
      )}

      {data.recentVisits.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            Останні візити
            <span className="hint" style={{ fontWeight: 400, marginLeft: 6 }}>
              · {Math.min(visitsShown, data.recentVisits.length)} з{" "}
              {data.recentVisits.length}
            </span>
          </h2>
          {data.recentVisits.slice(0, visitsShown).map((v: any) => {
            const d = new Date(v.startedAt);
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const hh = String(d.getHours()).padStart(2, "0");
            const mi = String(d.getMinutes()).padStart(2, "0");
            return (
              <div key={v.id} style={visitRow}>
                <div className="row">
                  <span style={{ fontWeight: 600, minWidth: 90 }}>
                    {dd}.{mm}
                    <span className="hint" style={{ marginLeft: 4, fontWeight: 400 }}>
                      {hh}:{mi}
                    </span>
                  </span>
                  <span style={{ flex: 1 }}>
                    {v.client
                      ? `${v.client.firstName} ${v.client.lastName ?? ""}`.trim()
                      : "—"}
                  </span>
                  <span>{STATUS_LABEL[v.status] ?? v.status}</span>
                </div>
                <div className="hint" style={{ fontSize: 11, marginTop: 1 }}>
                  {v.serviceLabel ?? SERVICE_LABEL[v.service] ?? v.service}
                  {v.price > 0 ? ` · ${uahFmt(v.price)} грн` : ""}
                </div>
              </div>
            );
          })}
          {visitsShown < data.recentVisits.length && (
            <button
              className="btn-secondary"
              onClick={() => setVisitsShown(visitsShown + PAGE_STEP)}
              style={loadMoreBtn}
            >
              ⬇ Завантажити ще (+
              {Math.min(PAGE_STEP, data.recentVisits.length - visitsShown)})
            </button>
          )}
        </div>
      )}

      {data.payouts.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>
            📒 Історія виплат
            <span className="hint" style={{ fontWeight: 400, marginLeft: 6 }}>
              · {Math.min(payoutsShown, data.payouts.length)} з{" "}
              {data.payouts.length}
            </span>
          </h2>
          {data.payouts.slice(0, payoutsShown).map((p: any) => (
            <div key={p.id} style={visitRow}>
              <div className="row">
                <span style={{ fontWeight: 600, minWidth: 80 }}>
                  {formatShortDate(p.paidAt)}
                </span>
                <span className="spacer" />
                <span style={{ fontWeight: 600, color: "#10b981" }}>
                  {uahFmt(p.amount)} грн
                </span>
              </div>
              <div className="hint" style={{ fontSize: 11, marginTop: 1 }}>
                40%: {uahFmt(p.fromPrices)} · чай: {uahFmt(p.fromTips)} · {p.cashEntryCount} записів
              </div>
            </div>
          ))}
          {payoutsShown < data.payouts.length && (
            <button
              className="btn-secondary"
              onClick={() => setPayoutsShown(payoutsShown + PAGE_STEP)}
              style={loadMoreBtn}
            >
              ⬇ Завантажити ще (+
              {Math.min(PAGE_STEP, data.payouts.length - payoutsShown)})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Developer-only: cash report viewer / editor ----

type CashSubmission = {
  id: string;
  occurredOn: string;
  sentDate: string;
  sentTime: string;
  sentFull: string;
  total: number;
  tips: number;
  entries: {
    id: string;
    service: string | null;
    amount: number;
    tip: number;
    discountPercent: number;
    isWarranty: boolean;
    isBarter: boolean;
    isBlogger?: boolean;
    rawMessage: string | null;
    inPayout: boolean;
  }[];
};

function fmtDayLabel(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// ---- All cash reports across masters (owner-only) ----

type CashSubmissionAll = CashSubmission & {
  master: { id: string; name: string } | null;
};

// A submission is "paid" once *every* entry has been folded into a payout.
// Anything else (zero or some entries paid) counts as "unpaid" — that's what
// the master is still owed for.
function isSubPaid(s: CashSubmission): boolean {
  return s.entries.length > 0 && s.entries.every((e) => e.inPayout);
}

type PaidFilter = "all" | "unpaid" | "paid";

function AllCashReportsView() {
  const [subs, setSubs] = useState<CashSubmissionAll[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PaidFilter>("all");

  function load() {
    api
      .allCashReports()
      .then((d) => setSubs(d as CashSubmissionAll[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(() => {
    load();
  }, []);

  if (error)
    return (
      <div className="card">
        <p>Помилка: {error}</p>
      </div>
    );
  if (!subs) return <div className="hint">Завантаження...</div>;
  if (subs.length === 0)
    return (
      <div className="card">
        <p style={{ margin: 0 }}>Поки нема жодного звіту каси.</p>
      </div>
    );

  const unpaidCount = subs.filter((s) => !isSubPaid(s)).length;
  const paidCount = subs.length - unpaidCount;

  const visible = subs.filter((s) => {
    if (filter === "unpaid") return !isSubPaid(s);
    if (filter === "paid") return isSubPaid(s);
    return true;
  });

  // Group submissions by master. "—" bucket for entries without a master.
  const groups = new Map<
    string,
    { masterName: string; subs: CashSubmissionAll[] }
  >();
  for (const s of visible) {
    const key = s.master?.id ?? "__unassigned";
    if (!groups.has(key)) {
      groups.set(key, { masterName: s.master?.name ?? "Без майстра", subs: [] });
    }
    groups.get(key)!.subs.push(s);
  }
  // Sort masters by most-recent submission (their first sub, since subs are
  // already date-desc inside each group).
  const ordered = [...groups.entries()].sort((a, b) =>
    a[1].subs[0]!.id < b[1].subs[0]!.id ? 1 : -1,
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <FilterChip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label={`Усі · ${subs.length}`}
        />
        <FilterChip
          active={filter === "unpaid"}
          onClick={() => setFilter("unpaid")}
          label={`💰 Неоплачені · ${unpaidCount}`}
        />
        <FilterChip
          active={filter === "paid"}
          onClick={() => setFilter("paid")}
          label={`✅ Оплачені · ${paidCount}`}
        />
      </div>
      <p className="hint" style={{ marginBottom: 8 }}>
        Тапни майстра → розгорнуться її звіти. Тапни звіт → розгорнуться
        послуги з повним редагуванням.
      </p>
      {ordered.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>Нічого не знайдено в цій категорії.</p>
        </div>
      ) : (
        ordered.map(([key, g]) => (
          <MasterReportsGroup
            key={key}
            masterName={g.masterName}
            subs={g.subs}
            onChanged={load}
          />
        ))
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "var(--tg-theme-button-color, #3390ec)" : "transparent",
        color: active
          ? "var(--tg-theme-button-text-color, #fff)"
          : "var(--tg-theme-text-color, #000)",
        border: "1px solid var(--tg-theme-hint-color, #999)",
        borderRadius: 999,
        padding: "4px 12px",
        fontSize: 13,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function MasterReportsGroup({
  masterName,
  subs,
  onChanged,
}: {
  masterName: string;
  subs: CashSubmissionAll[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const totalAmount = subs.reduce((s, x) => s + x.total, 0);
  const totalTips = subs.reduce((s, x) => s + x.tips, 0);
  const unpaidHere = subs.filter((x) => !isSubPaid(x)).length;
  return (
    <div className="card">
      <button onClick={() => setOpen(!open)} style={dayHeader}>
        {open ? "▾" : "▸"} <b>{masterName}</b> · {subs.length} звітів ·{" "}
        {uahFmt(totalAmount)} грн
        {totalTips > 0 ? ` · 💸 ${uahFmt(totalTips)}` : ""}
        {unpaidHere > 0 ? ` · 💰 ${unpaidHere} неоплач.` : ""}
      </button>
      {open && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {subs.map((s) => (
            <CashSubmissionCard
              key={s.id}
              sub={s}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MasterCashReports({
  masterId,
  masterName,
  onBack,
}: {
  masterId: string;
  masterName: string;
  onBack: () => void;
}) {
  const [subs, setSubs] = useState<CashSubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api
      .masterCashReports(masterId)
      .then((d) => setSubs(d as CashSubmission[]))
      .catch((e) => setError(String(e)));
  }
  useEffect(() => {
    load();
  }, [masterId]);

  if (error) {
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="card">
          <p>Помилка: {error}</p>
        </div>
      </div>
    );
  }
  if (!subs) {
    return (
      <div>
        <BackBtn onBack={onBack} />
        <div className="hint">Завантаження...</div>
      </div>
    );
  }

  return (
    <div>
      <BackBtn onBack={onBack} />
      <div className="card">
        <h1 style={{ margin: "0 0 4px", fontSize: 19 }}>📋 Звіти каси</h1>
        <div className="hint">
          {masterName} · {subs.length} повідомлень від майстра
        </div>
      </div>
      {subs.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>Касових звітів ще нема.</p>
        </div>
      ) : (
        subs.map((s) => (
          <CashSubmissionCard
            key={s.id}
            sub={s}
            onChanged={load}
          />
        ))
      )}
    </div>
  );
}

function CashSubmissionCard({
  sub,
  onChanged,
}: {
  sub: CashSubmission;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const paid = isSubPaid(sub);

  async function unpay() {
    if (busy) return;
    if (
      !window.confirm(
        `Відмінити оплачене за ${fmtDayLabel(sub.occurredOn)}?\n\n` +
          `Усі ${sub.entries.length} послуг (${uahFmt(sub.total)} грн) ` +
          `повернуться в «до виплати». Сума виплати перерахується, а якщо ` +
          `це був єдиний день у ній — виплата видалиться повністю.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.unpayCashReport(sub.id);
      onChanged();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <button onClick={() => setOpen(!open)} style={dayHeader}>
        {open ? "▾" : "▸"} {paid ? "✅" : "💰"}{" "}
        {fmtDayLabel(sub.occurredOn)} · {sub.entries.length}{" "}
        послуг · {uahFmt(sub.total)} грн · 🕐 {sub.sentTime}
      </button>
      {open && (
        <>
          <div className="hint" style={{ fontSize: 11, margin: "6px 0" }}>
            Надіслано: {sub.sentFull}
            {sub.tips > 0 ? ` · 💸 чайові ${uahFmt(sub.tips)}` : ""}
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {sub.entries.map((e) => (
              <EditableCashEntry key={e.id} entry={e} onChanged={onChanged} />
            ))}
          </div>
          {paid && (
            <button
              type="button"
              onClick={unpay}
              disabled={busy}
              style={legacyUnpayBtn}
            >
              ↩️ Відмінити оплачене
            </button>
          )}
        </>
      )}
    </div>
  );
}

const legacyUnpayBtn: React.CSSProperties = {
  width: "100%",
  marginTop: 10,
  height: 42,
  borderRadius: 10,
  border: "1.5px dashed rgba(217, 148, 32, 0.55)",
  background: "rgba(217, 148, 32, 0.08)",
  color: "#B67611",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
};

function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ flex: 1 }}>
      <div className="hint" style={{ fontSize: 11, marginBottom: 2 }}>
        {label}
      </div>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
        style={cashFieldFull}
      />
    </label>
  );
}

function EditableCashEntry({
  entry,
  onChanged,
}: {
  entry: CashSubmission["entries"][number];
  onChanged: () => void;
}) {
  const [service, setService] = useState(entry.service ?? "");
  const [amount, setAmount] = useState(String(entry.amount));
  const [tip, setTip] = useState(String(entry.tip));
  const [discount, setDiscount] = useState(String(entry.discountPercent));
  const [busy, setBusy] = useState(false);

  if (entry.inPayout) {
    return (
      <div style={cashEntryBox}>
        <div className="row">
          <span style={{ fontWeight: 600, flex: 1 }}>
            {entry.service ?? "—"}
          </span>
          <span style={{ fontWeight: 700 }}>{uahFmt(entry.amount)} грн</span>
        </div>
        <div className="hint" style={{ fontSize: 11, marginTop: 2 }}>
          🔒 У виплаті — редагувати не можна
          {entry.tip > 0 ? ` · 💸 ${uahFmt(entry.tip)}` : ""}
        </div>
      </div>
    );
  }

  const dirty =
    service !== (entry.service ?? "") ||
    Number(amount || 0) !== entry.amount ||
    Number(tip || 0) !== entry.tip ||
    Number(discount || 0) !== entry.discountPercent;

  async function save() {
    if (busy || !dirty) return;
    if (!service.trim()) {
      alert("Назва послуги не може бути порожньою");
      return;
    }
    setBusy(true);
    try {
      await api.editCashEntry(entry.id, {
        service: service.trim(),
        amount: Number(amount || 0),
        tip: Number(tip || 0),
        discountPercent: Number(discount || 0),
      });
      onChanged();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (busy) return;
    if (!window.confirm(`Видалити запис «${service || "?"} — ${amount}»?`))
      return;
    setBusy(true);
    try {
      await api.deleteCash(entry.id);
      onChanged();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={cashEntryBox}>
      {(entry.isWarranty || entry.isBlogger) && (
        <div style={{ marginBottom: 6 }}>
          {entry.isWarranty && (
            <span style={warrantyBadge}>🛡 гарантія · 0 в касу</span>
          )}
          {entry.isBlogger && (
            <span style={bloggerBadge}>📣 блогер (по бартеру)</span>
          )}
        </div>
      )}
      <input
        type="text"
        value={service}
        onChange={(e) => setService(e.target.value)}
        placeholder="Послуга"
        style={cashFieldFull}
      />
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <NumField label="Сума" value={amount} onChange={setAmount} />
        <NumField label="Знижка %" value={discount} onChange={setDiscount} />
        <NumField label="Чай" value={tip} onChange={setTip} />
      </div>
      {entry.rawMessage && (
        <div className="hint" style={{ fontSize: 11, marginTop: 4 }}>
          Оригінал: «{entry.rawMessage}»
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button
          onClick={save}
          disabled={busy || !dirty}
          style={{ ...cashSaveBtn, opacity: busy || !dirty ? 0.5 : 1 }}
        >
          {busy ? "..." : dirty ? "Зберегти зміни" : "Збережено"}
        </button>
        <button onClick={del} disabled={busy} style={cashDelBtn}>
          🗑
        </button>
      </div>
    </div>
  );
}

function PayoutPanel({
  paidOut,
  pendingFromPrices,
  pendingTips,
  onPayOut,
}: {
  paidOut: number;
  pendingFromPrices: number;
  pendingTips: number;
  onPayOut: () => void;
}) {
  const pendingTotal = pendingFromPrices + pendingTips;
  const bothPending = pendingFromPrices > 0 && pendingTips > 0;
  const hasPending = pendingTotal > 0;

  const btnLabel =
    bothPending
      ? `💵 Виплатити ${uahFull(pendingFromPrices)} грн + ${uahFull(pendingTips)} грн чайові`
      : pendingTips > 0
        ? `💵 Виплатити ${uahFull(pendingTips)} грн чайових`
        : `💵 Виплатити ${uahFull(pendingFromPrices)} грн`;

  return (
    <div className="card" style={{ padding: 12, marginBottom: 12 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 16 }}>💵 Виплати майстру</h2>

      <div className="row" style={{ marginBottom: 6 }}>
        <span className="hint">Виплачено</span>
        <span className="spacer" />
        <span style={{ color: "#0ea5e9", fontWeight: 600 }}>
          {uahFull(paidOut)} грн
        </span>
      </div>

      <div className="row" style={{ marginBottom: bothPending ? 4 : 10 }}>
        <span className="hint">До виплати</span>
        <span className="spacer" />
        <span
          style={{
            color: hasPending ? "#f59e0b" : "var(--text)",
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          {uahFull(pendingTotal)} грн
        </span>
      </div>

      {bothPending && (
        <div
          className="hint"
          style={{ fontSize: 12, marginLeft: 12, marginBottom: 10 }}
        >
          • {uahFull(pendingFromPrices)} грн з послуг
          <br />
          • 💸 {uahFull(pendingTips)} грн чайові
        </div>
      )}

      {hasPending && (
        <button onClick={onPayOut} style={{ ...payBtn, marginBottom: 0 }}>
          {btnLabel}
        </button>
      )}
    </div>
  );
}

function StatBox({
  label,
  value,
  hint,
  color = "var(--text)",
}: {
  label: string;
  value: string | number;
  hint?: string;
  color?: string;
}) {
  return (
    <div style={statBox}>
      <div className="hint" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>
        {typeof value === "number" ? value.toLocaleString("uk-UA") : value}
      </div>
      {hint && (
        <div className="hint" style={{ fontSize: 11, marginTop: 2 }}>{hint}</div>
      )}
    </div>
  );
}

function BackBtn({ onBack }: { onBack: () => void }) {
  return (
    <button onClick={onBack} style={backBtn}>
      ← Назад
    </button>
  );
}

const inputStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: 10,
  marginBottom: 8,
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 8,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
};

const buttonStyle: CSSProperties = {
  padding: "10px 16px",
  background: "var(--button)",
  color: "var(--button-text)",
  border: 0,
  borderRadius: 8,
  fontSize: 14,
  cursor: "pointer",
};

const masterCardWrap: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "stretch",
  marginBottom: 8,
};

const starBtn: CSSProperties = {
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: 0,
  borderRadius: 12,
  padding: "0 12px",
  fontSize: 22,
  cursor: "pointer",
  minWidth: 44,
};

const masterCardBtn: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: 0,
  borderRadius: 12,
  padding: 12,
  marginBottom: 8,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 14,
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: 8,
  marginBottom: 12,
};

const breakdownGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 8,
  marginBottom: 12,
};

const statBox: CSSProperties = {
  background: "var(--secondary-bg)",
  borderRadius: 10,
  padding: 10,
};

const topRow: CSSProperties = {
  padding: "6px 0",
  borderBottom: "1px solid rgba(127,127,127,0.15)",
  fontSize: 13,
};

const visitRow: CSSProperties = {
  background: "var(--secondary-bg)",
  borderRadius: 8,
  padding: 8,
  marginBottom: 4,
};

const loadMoreBtn: CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  marginTop: 8,
  fontSize: 14,
  fontWeight: 600,
};

const backBtn: CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--link)",
  fontSize: 14,
  padding: "4px 0",
  marginBottom: 4,
  cursor: "pointer",
};

const photoGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 4,
};

const photoCell: CSSProperties = {
  display: "block",
  aspectRatio: "1 / 1",
  overflow: "hidden",
  borderRadius: 8,
  background: "var(--secondary-bg)",
};

const photoImg: CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const payBtn: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "12px 14px",
  background: "#10b981",
  color: "white",
  border: 0,
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  marginBottom: 12,
};

const reportsBtn: CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 4,
  padding: "8px 12px",
  background: "var(--secondary-bg)",
  color: "var(--text)",
  border: "1px dashed rgba(127,127,127,0.4)",
  borderRadius: 8,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "inherit",
};

const dayHeader: CSSProperties = {
  width: "100%",
  textAlign: "left",
  background: "transparent",
  color: "var(--text)",
  border: 0,
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  padding: 0,
  fontFamily: "inherit",
};

const barterBadge: CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 600,
  background: "rgba(245,158,11,0.2)",
  color: "#b45309",
};

const warrantyBadge: CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 600,
  background: "rgba(59,130,246,0.15)",
  color: "#2563eb",
  marginLeft: 6,
};

const bloggerBadge: CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 600,
  background: "rgba(168,85,247,0.18)",
  color: "#7e22ce",
  marginLeft: 6,
};

const cashEntryBox: CSSProperties = {
  background: "var(--bg)",
  border: "1px solid rgba(127,127,127,0.2)",
  borderRadius: 8,
  padding: 10,
};

const cashFieldFull: CSSProperties = {
  display: "block",
  width: "100%",
  padding: 8,
  border: "1px solid rgba(127,127,127,0.3)",
  borderRadius: 6,
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
  boxSizing: "border-box",
};

const cashSaveBtn: CSSProperties = {
  flex: 1,
  padding: "8px 12px",
  background: "var(--button)",
  color: "var(--button-text)",
  border: 0,
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const cashDelBtn: CSSProperties = {
  padding: "8px 14px",
  background: "transparent",
  color: "#e74c3c",
  border: "1px solid #e74c3c",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
};
